/**
 * Product Dual Scroll Controller
 *
 * 상품 상세페이지의 좌측(미디어)과 우측(상품정보) 영역에 개별 스크롤을 적용합니다.
 *
 * 동작 흐름:
 * 1. 마우스 커서가 위치한 영역이 우선적으로 스크롤
 * 2. 해당 영역이 끝에 도달하면 → 반대쪽 영역이 스크롤
 * 3. 양쪽 모두 끝에 도달하면 → 다음 섹션으로 페이지 스크롤
 *
 * 헤더 연동:
 * - 개별 패널 스크롤 중에는 헤더 투명 유지
 * - 양쪽 끝 도달 후 페이지 스크롤 시 헤더 원래 동작 복원
 */
(function () {
  'use strict';

  var DESKTOP_BREAKPOINT = 1024;
  var SCROLL_THRESHOLD = 1; // 끝 도달 판정 여유값 (px)

  function ProductDualScroll() {
    /** @type {HTMLElement | null} */
    var container = document.querySelector('.product-information__grid');
    if (!container) return;

    /** @type {HTMLElement | null} */
    var mediaPanelRaw = container.querySelector('.product-information__media');
    /** @type {HTMLElement | null} */
    var detailsPanelRaw = container.querySelector('.product-details');

    if (!mediaPanelRaw || !detailsPanelRaw) return;

    // 린트 에러 방지를 위해 Non-null 타입으로 확정
    var mediaPanel = mediaPanelRaw;
    var detailsPanel = detailsPanelRaw;

    // 헤더 엘리먼트 참조
    /** @type {HTMLElement | null} */
    var headerComponent = document.getElementById('header-component');

    /** @type {HTMLElement | null} */
    var hoveredPanel = null;
    /** @type {boolean} */
    var isDualScrolling = false; // 현재 듀얼 스크롤 중인지 플래그
    /** @type {boolean} */
    var isPanelsLocked = false; // 화면이 듀얼 영역을 벗어났을 때 내부 스크롤 무시용 락

    // 데스크톱인지 확인
    if (window.innerWidth < DESKTOP_BREAKPOINT) return;

    // --- 헤더 투명 제어 ---

    /**
     * 듀얼 스크롤 중에는 헤더를 투명 상태로 강제 유지
     * @param {boolean} transparent
     */
    function setHeaderTransparent(transparent) {
      if (!headerComponent) return;

      if (transparent) {
        if (!isDualScrolling) {
          isDualScrolling = true;
          headerComponent.setAttribute('data-dual-scroll-active', '');
        }
      } else {
        if (isDualScrolling) {
          isDualScrolling = false;
          headerComponent.removeAttribute('data-dual-scroll-active');
        }
      }
    }

    /**
     * 양쪽 패널 모두 상단(scrollTop === 0)인지 체크
     * @returns {boolean}
     */
    function areBothPanelsAtTop() {
      return mediaPanel.scrollTop <= SCROLL_THRESHOLD &&
             detailsPanel.scrollTop <= SCROLL_THRESHOLD;
    }

    /**
     * 패널의 콘텐츠가 스크롤이 필요한 만큼 큰지 확인
     * @param {HTMLElement} panel
     * @returns {boolean}
     */
    function hasScrollableContent(panel) {
      return panel.scrollHeight > panel.clientHeight + SCROLL_THRESHOLD;
    }

    /**
     * 패널이 주어진 방향으로 더 스크롤 가능한지 체크
     * @param {HTMLElement} panel
     * @param {number} direction - 양수: 아래로, 음수: 위로
     * @returns {boolean}
     */
    function canScroll(panel, direction) {
      if (direction > 0) {
        return panel.scrollTop + panel.clientHeight < panel.scrollHeight - SCROLL_THRESHOLD;
      } else {
        return panel.scrollTop > SCROLL_THRESHOLD;
      }
    }

    /**
     * wheel 이벤트 target 기반으로 패널 판별
     * @param {EventTarget | null} target
     * @returns {HTMLElement | null}
     */
    function resolvePanelFromTarget(target) {
      if (!(target instanceof Element)) return null;
      var panel = target.closest('.product-details, .product-information__media');
      if (panel === mediaPanel || panel === detailsPanel) {
        return panel;
      }
      return null;
    }

    /**
     * target 우선 + hover fallback 기반 primary/secondary 패널 판별
     * @param {EventTarget | null} target
     * @returns {{ primary: HTMLElement, secondary: HTMLElement }}
     */
    function getPanels(target) {
      var targetPanel = resolvePanelFromTarget(target);
      var primary = targetPanel || hoveredPanel || mediaPanel;
      var secondary = (primary === mediaPanel) ? detailsPanel : mediaPanel;
      return { primary: primary, secondary: secondary };
    }

    // --- 이벤트 바인딩 ---

    mediaPanel.addEventListener('mouseenter', function () {
      hoveredPanel = mediaPanel;
    });

    detailsPanel.addEventListener('mouseenter', function () {
      hoveredPanel = detailsPanel;
    });

    container.addEventListener('mouseleave', function () {
      hoveredPanel = null;
    });

    /**
     * 그리드가 아직 sticky 위치에 도달하지 않았는지 판별
     * (announcement-bar 등이 아직 화면에 보이는 상태)
     * @returns {boolean}
     */
    function isContainerBeforeStickyPosition() {
      if (!container) return false;
      var headerHeight = parseFloat(
        getComputedStyle(document.body).getPropertyValue('--header-height')
      ) || 0;
      var containerTop = container.getBoundingClientRect().top;
      return containerTop > headerHeight + SCROLL_THRESHOLD;
    }

    // --- Wheel 이벤트 핸들러 ---
    container.addEventListener('wheel', function (e) {
      if (window.innerWidth < DESKTOP_BREAKPOINT) return;

      var event = /** @type {WheelEvent} */ (e);
      var target = event.target;

      // 다이얼로그(팝업)가 열려있으면 듀얼 스크롤 비활성화
      if (target instanceof Element && target.closest('dialog[open]')) return;

      var deltaY = event.deltaY;
      if (deltaY === 0) return;

      var direction = deltaY > 0 ? 1 : -1;

      // 단계 0: announcement-bar가 아직 보이면 페이지 스크롤 우선
      if (direction > 0 && isContainerBeforeStickyPosition()) {
        setHeaderTransparent(true);
        return;
      }

      // 화면이 듀얼 영역 하단 아래로 내려가 패널이 CSS 잠금 처리된 상태면, 
      // JS 스크롤 개입(preventDefault 등)을 전면 차단하여 정상적인 윈도우 휠 이벤트로 넘깁니다.
      if (isPanelsLocked) return;

      var panels = getPanels(target);
      var primary = panels.primary;
      var secondary = panels.secondary;

      // 단계 1: 커서 쪽(primary) 패널이 스크롤 가능하면 스크롤
      if (hasScrollableContent(primary) && canScroll(primary, direction)) {
        event.preventDefault();
        primary.scrollTop += deltaY;
        setHeaderTransparent(true);
        return;
      }

      // 단계 2: primary가 끝에 도달했으면 secondary 패널 스크롤
      if (hasScrollableContent(secondary) && canScroll(secondary, direction)) {
        event.preventDefault();
        secondary.scrollTop += deltaY;
        setHeaderTransparent(true);
        return;
      }

      // 단계 3: 양쪽 모두 끝에 도달 → 기본 스크롤 허용
      setHeaderTransparent(false);
    }, { passive: false });

    // --- 페이지 스크롤 감시 (역방향 스크롤 시 투명 복원 및 overflow 복구) ---
    window.addEventListener('scroll', function () {
      if (window.innerWidth < DESKTOP_BREAKPOINT) return;
      if (!container) return;

      var stickyTop = parseFloat(getComputedStyle(container).getPropertyValue('top')) || 0;
      var gridTop = container.getBoundingClientRect().top;

      // 상태 변화가 있을 때만 DOM 업데이트 (성능 최적화)
      if (gridTop < stickyTop - SCROLL_THRESHOLD) {
        if (!isPanelsLocked) {
          mediaPanel.style.overflowY = 'hidden';
          detailsPanel.style.overflowY = 'hidden';
          isPanelsLocked = true;
        }
      } else {
        if (isPanelsLocked) {
          mediaPanel.style.overflowY = '';
          detailsPanel.style.overflowY = '';
          isPanelsLocked = false;
        }
      }

      var scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      if (scrollTop <= SCROLL_THRESHOLD && areBothPanelsAtTop()) {
        setHeaderTransparent(true);
      }
    });

    // --- 리사이즈 핸들러 ---
    window.addEventListener('resize', function () {
      if (window.innerWidth < DESKTOP_BREAKPOINT) {
        mediaPanel.scrollTop = 0;
        detailsPanel.scrollTop = 0;
        setHeaderTransparent(false);
      }
    });

    // 초기 상태: 페이지 최상단이면 투명 유지
    var initialScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    if (initialScrollTop <= SCROLL_THRESHOLD) {
      setHeaderTransparent(true);
    }
  }

  // DOM 준비 완료 후 초기화
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ProductDualScroll);
  } else {
    ProductDualScroll();
  }
})();
